// frontend/src/components/ImportChecklist.tsx
// Per-cycle carrier checklist for the Imports page. "This cycle" = batches
// created after the system's most recent closed pay run — closing a run
// resets the checklist. All computation is client-side from queries the
// page already runs; only the expect_statement mute persists (on the
// carrier record, absent = expected).
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, AlertCircle, Circle, EyeOff, Loader2 } from 'lucide-react';
import { api, fmt, type System, type Batch, type Carrier, type PayRun } from '../lib/api';
import { useToast } from '../contexts/ToastContext';

export type ChecklistItem = {
  carrier: string;
  displayName: string;
  status: 'loaded' | 'attention' | 'waiting';
  detail: string;
};

export function computeChecklist(
  carriers: Carrier[],
  batches: Batch[],
  payRuns: PayRun[],
): { items: ChecklistItem[]; muted: Carrier[] } {
  const cycleStart = payRuns
    .filter((p) => p.status === 'closed' && p.closed_at)
    .reduce((max, p) => ((p.closed_at as string) > max ? (p.closed_at as string) : max), '');
  const expected = carriers.filter((c) => c.expect_statement !== false);
  const muted = carriers.filter((c) => c.expect_statement === false);

  const items = expected
    .map((c) => {
      const cycleBatches = batches.filter(
        (b) => b.carrier === c.carrier && b.created_at > cycleStart,
      );
      const loaded = cycleBatches.find((b) => b.status === 'complete' && b.row_count > 0);
      if (loaded) {
        return {
          carrier: c.carrier,
          displayName: c.display_name,
          status: 'loaded' as const,
          detail: `${loaded.row_count.toLocaleString()} rows · ${fmt.date(loaded.created_at)}`,
        };
      }
      if (cycleBatches.length > 0) {
        const failed = cycleBatches.some((b) => b.status === 'failed');
        return {
          carrier: c.carrier,
          displayName: c.display_name,
          status: 'attention' as const,
          detail: failed ? 'import failed — see below' : '0 rows imported — check the file',
        };
      }
      return {
        carrier: c.carrier,
        displayName: c.display_name,
        status: 'waiting' as const,
        detail: '',
      };
    })
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  return { items, muted };
}

const STATUS_ICON = {
  loaded: <CheckCircle2 size={15} className="text-green-500 shrink-0" />,
  attention: <AlertCircle size={15} className="text-amber-500 shrink-0" />,
  waiting: <Circle size={15} className="shrink-0" style={{ color: 'var(--color-border)' }} />,
};

export default function ImportChecklist({
  sys,
  carriers,
  batches,
  payRuns,
}: {
  sys: System;
  carriers: Carrier[];
  batches: Batch[];
  payRuns: PayRun[];
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState<string | null>(null);

  const { items, muted } = computeChecklist(carriers, batches, payRuns);
  if (items.length === 0 && muted.length === 0) return null;

  const setExpect = async (carrier: string, expect: boolean) => {
    setSaving(carrier);
    try {
      await api.updateCarrier(sys, carrier, { expect_statement: expect });
      await queryClient.invalidateQueries({ queryKey: ['carriers', sys] });
    } catch (err) {
      toast.error(`Update failed: ${(err as Error).message}`);
    } finally {
      setSaving(null);
    }
  };

  const loadedCount = items.filter((i) => i.status === 'loaded').length;

  return (
    <div className="card">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
          This cycle's statements
        </h3>
        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          {loadedCount} of {items.length} loaded
        </span>
      </div>
      <ul className="mt-3 grid gap-x-6 gap-y-1.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))' }}>
        {items.map((item) => (
          <li key={item.carrier} className="group flex items-center gap-2 text-sm">
            {STATUS_ICON[item.status]}
            <span style={{ color: item.status === 'waiting' ? 'var(--color-text-muted)' : 'var(--color-text)' }}>
              {item.displayName}
            </span>
            {item.detail && (
              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                {item.detail}
              </span>
            )}
            <button
              onClick={() => setExpect(item.carrier, false)}
              disabled={saving === item.carrier}
              className="btn-icon opacity-0 group-hover:opacity-100 transition-opacity ml-auto"
              style={{ color: 'var(--color-text-muted)' }}
              title="Don't expect a statement from this carrier"
            >
              {saving === item.carrier ? <Loader2 size={12} className="animate-spin" /> : <EyeOff size={12} />}
            </button>
          </li>
        ))}
      </ul>
      {muted.length > 0 && (
        <p className="mt-3 text-xs" style={{ color: 'var(--color-text-muted)' }}>
          Not expected:{' '}
          {muted.map((c, i) => (
            <span key={c.carrier}>
              {i > 0 && ', '}
              <button
                onClick={() => setExpect(c.carrier, true)}
                disabled={saving === c.carrier}
                className="underline decoration-dotted"
                title="Expect a statement from this carrier again"
              >
                {c.display_name}
              </button>
            </span>
          ))}
        </p>
      )}
    </div>
  );
}
